import type { InsertTest } from "@shared/schema";

// Dev convenience: a hand-crafted 10-question sample test. Two uses:
//  1. The Dashboard "Add sample test" button POSTs this to /api/tests so a
//     teacher can play with the open/preview/edit/download flow.
//  2. The failed-generation screen loads it into the review-and-revise step
//     (when Blacksmith is down) so people can try editing items.
//
// Every question carries blacksmithSessionId "dev-sample"; the server's
// /api/socrates-score endpoint returns canned Socrates results for that id,
// with a few questions intentionally failing so the failing-Socrates UI shows.
//
// Safe to delete once real seed data exists.

export const DEV_SAMPLE_SESSION_ID = "dev-sample";

export function buildSampleTestPayload(): InsertTest {
  const s = DEV_SAMPLE_SESSION_ID;
  return {
    name: "Sample Test: Linear Equations & Word Problems",
    directions:
      "Read each question carefully and choose the best answer. This test has 10 total items. When you are finished, review your answers if time allows.",
    config: {
      audience: "Middle School Students",
      readingLevel: "7",
      testLengthType: "items",
      testLengthValue: 10,
      materials: [],
      lessonPlans: [],
      subject: "math",
      additionalContext:
        "Focus on real-world applications and multi-step reasoning where appropriate.",
    },
    blueprint: {
      topics: [
        {
          id: "topic-linear",
          name: "Linear Equations",
          percentage: 30,
          knowledge: 1,
          understanding: 1,
          higherOrderThinking: 1,
        },
        {
          id: "topic-word",
          name: "Word Problems",
          percentage: 40,
          knowledge: 1,
          understanding: 2,
          higherOrderThinking: 1,
        },
        {
          id: "topic-graphing",
          name: "Graphing & Slope",
          percentage: 30,
          knowledge: 1,
          understanding: 2,
          higherOrderThinking: 0,
        },
      ],
    },
    questions: [
      {
        id: "q-1",
        topicId: "topic-linear",
        level: "Knowledge",
        stem: "Solve for x: 2x + 6 = 14",
        answers: ["x = 2", "x = 4", "x = 8", "x = 10"],
        correctAnswer: 1,
        locked: false,
        stemLocked: false,
        answersLocked: false,
        blacksmithSessionId: s,
      },
      {
        id: "q-2",
        topicId: "topic-linear",
        level: "Understanding",
        stem: "Which equation has no solution?",
        answers: [
          "3x + 1 = 3x - 1",
          "3x + 1 = 3x + 1",
          "2x + 4 = 4x - 2",
          "x = 5",
        ],
        correctAnswer: 0,
        locked: false,
        stemLocked: false,
        answersLocked: false,
        blacksmithSessionId: s,
      },
      {
        id: "q-3",
        topicId: "topic-linear",
        level: "Higher-Order Thinking",
        stem: "If 4(x − 3) = 2x + 6, what is the value of x?",
        answers: ["3", "6", "9", "12"],
        correctAnswer: 2,
        locked: false,
        stemLocked: false,
        answersLocked: false,
        blacksmithSessionId: s,
      },
      {
        id: "q-4",
        topicId: "topic-word",
        level: "Understanding",
        stem: "Maya has $48. She wants to buy notebooks that cost $6 each. How many notebooks can she buy?",
        answers: ["6", "7", "8", "9"],
        correctAnswer: 2,
        locked: false,
        stemLocked: false,
        answersLocked: false,
        blacksmithSessionId: s,
      },
      {
        id: "q-5",
        topicId: "topic-word",
        level: "Understanding",
        stem: "A bus travels 60 miles in 1.5 hours. At the same rate, how far will it travel in 4 hours?",
        answers: ["120 miles", "150 miles", "160 miles", "180 miles"],
        correctAnswer: 2,
        locked: false,
        stemLocked: false,
        answersLocked: false,
        blacksmithSessionId: s,
      },
      {
        id: "q-6",
        topicId: "topic-word",
        level: "Higher-Order Thinking",
        stem: "A pool fills at 12 gallons per minute. The pool currently has 80 gallons and holds 320 gallons total. After how many minutes will the pool be full?",
        answers: ["18 minutes", "20 minutes", "22 minutes", "24 minutes"],
        correctAnswer: 1,
        locked: false,
        stemLocked: false,
        answersLocked: false,
        blacksmithSessionId: s,
      },
      {
        id: "q-7",
        topicId: "topic-graphing",
        level: "Knowledge",
        stem: "What is the slope of the line passing through the points (1, 2) and (4, 11)?",
        answers: ["1", "2", "3", "4"],
        correctAnswer: 2,
        locked: false,
        stemLocked: false,
        answersLocked: false,
        blacksmithSessionId: s,
      },
      {
        id: "q-8",
        topicId: "topic-graphing",
        level: "Understanding",
        stem: "Which line is parallel to y = 2x + 5?",
        answers: [
          "y = −2x + 1",
          "y = 2x − 3",
          "y = (1/2)x + 5",
          "y = −(1/2)x + 5",
        ],
        correctAnswer: 1,
        locked: false,
        stemLocked: false,
        answersLocked: false,
        blacksmithSessionId: s,
      },
      {
        id: "q-9",
        topicId: "topic-word",
        level: "Knowledge",
        stem: "Which expression is NOT a correct way to represent 'five less than a number n'?",
        answers: ["n − 5", "5 − n", "n + (−5)", "the difference of n and 5"],
        correctAnswer: 1,
        locked: false,
        stemLocked: false,
        answersLocked: false,
        blacksmithSessionId: s,
      },
      {
        id: "q-10",
        topicId: "topic-graphing",
        level: "Understanding",
        stem: "A line has a slope of 0. Which statement is true?",
        answers: [
          "The line is horizontal",
          "The line is vertical",
          "The line passes through the origin",
          "All of the above",
        ],
        correctAnswer: 0,
        locked: false,
        stemLocked: false,
        answersLocked: false,
        blacksmithSessionId: s,
      },
    ],
  };
}
